import { times, uniqueId } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { importService } from 'bom/services';
import { useBomPartsImportContext } from 'bom/states';
import { AgGridServerTable, Button, SlidingPanel } from 'core/components';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridDataRow, AgGridGetRowsParams, GetManyResponse } from 'core/types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  initialHeaderRowIndex: number;
  onSelectHeaderRowClick: (headerRowIndex: number) => void;
}

const PAGE_SIZE = 30;

export const HeaderRowList = ({
  isOpen,
  onClose,
  initialHeaderRowIndex,
  onSelectHeaderRowClick,
}: IProps) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(initialHeaderRowIndex + 1);

  const { t } = useTranslation();

  const { partImportId, numberOfColumns } = useBomPartsImportContext();

  const onFirstDataRendered = useCallback(
    ({ api }) => {
      api.forEachNode((node) => {
        if (node.rowIndex === initialHeaderRowIndex + 1) {
          node.setSelected(true);

          api.ensureIndexVisible(node.rowIndex, 'middle');
        }
      });
    },
    [initialHeaderRowIndex]
  );

  const onSelectionChanged = useCallback(({ api }) => {
    const gridSelectedRows = api?.getSelectedNodes();

    setSelectedRowIndex(gridSelectedRows[0]?.rowIndex);
  }, []);

  const onSubmit = () => {
    onSelectHeaderRowClick(selectedRowIndex - 1);
    onClose();
  };

  const defaultColDef = useMemo(
    () => ({
      filter: false,
      floatingFilter: false,
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      minWidth: 100,
      maxWidth: 200,
      enableRowGroup: false,
      headerClass: 'hidden',
    }),
    []
  );

  const columnDefs = useMemo(() => {
    return times(numberOfColumns + 1, (index) => ({
      colId: index.toString(),
      headerName: '',
      field: `col${index}`,
    }));
  }, [numberOfColumns]);

  const getRows = useCallback(
    async ({ page, pageSize }: AgGridGetRowsParams) => {
      const { records, count } = await importService.getImportPreviewRows({
        partImport: partImportId,
        includeHeaderRow: 0,
        headerRowIndex: -1,
        includeEmptyRows: 1,
        page,
        pageSize,
      });

      let noHeaderRowRecord: string[] = [];
      if (1 === page) {
        noHeaderRowRecord = [t('messageImportNoHeaderRow')];
      }

      const recordsMapped = [noHeaderRowRecord, ...(records || [])]?.map((rowData: string[]) => {
        const row = rowData?.reduce((rowMapped, value, index) => {
          rowMapped[`col${index}`] = value;

          return rowMapped;
        }, {} as AgGridDataRow);

        row.id = Number(uniqueId());

        return row;
      });

      return Promise.resolve({
        records: recordsMapped,
        count,
      } as GetManyResponse<AgGridDataRow>);
    },
    [partImportId, t]
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={t('titleSelectHeaderRow')}
      contentStyle={{ width: '1000px', paddingBottom: '24px' }}
      autoWidth
    >
      <AgGridServerTableProvider
        tableId="selectHeaderRowTable"
        entityType="Row"
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        getRows={getRows}
        getRowId={({ data }) => data?.id?.toString()}
        onSelectionChanged={onSelectionChanged}
        pageSize={PAGE_SIZE}
        sideBar={{}}
        autoSizeStrategy={{
          type: 'fitCellContents',
          skipHeader: true,
        }}
        hasQuickSearch={false}
        headerHeight={0}
        pivotHeaderHeight={0}
        hasTopActions={false}
        selectionMode="singleRow"
        enableClickSelection
        onFirstDataRendered={onFirstDataRendered}
      >
        <AgGridServerTable />

        <Button text={t('buttonLabelDone')} onClick={onSubmit} sx={{ width: 'fit-content' }} />
      </AgGridServerTableProvider>
    </SlidingPanel>
  );
};
